import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackageBoardWorkitem, HarmonyWorkitem } from '@rhythmic/db/types'
import type { CreateKanbanBoardItemPayload } from '@rhythmic/types'
import { CreateKanbanBoardItemSchema, generateUuid, KanbanContextSchema, WorkitemSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getBoardItemsCollection, getHarmonyItemsCollection, getWorkitemsCollection } from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext.createBoardItem')

export default function handleCreateBoardItem(
  payload: CreateKanbanBoardItemPayload,
  context:
    | {
        boardId: string
        packageId: string
        workspaceId: string
        teamId: string
      }
    | undefined,
): Transaction {
  const createPayload = CreateKanbanBoardItemSchema.parse(payload)
  const createContext = KanbanContextSchema.parse(context)

  const boardItemsCollection = getBoardItemsCollection(createContext.boardId)
  const packageItemsCollection = getHarmonyItemsCollection(createContext.packageId)
  const workitemsCollection = getWorkitemsCollection(createContext.teamId)

  logger.debug('Creating board item', {
    boardId: createContext.boardId,
    packageId: createContext.packageId,
    workspaceId: createContext.workspaceId,
    teamId: createContext.teamId,
    columnId: createContext.columnId,
    payload: createPayload,
  })

  const harmonyWorkitemId = `hwi_${createId()}`
  const boardWorkitemId = `bwi_${createId()}`
  const position = createPayload.position ?? 0

  const workitemRecord = WorkitemSchema.parse({
    ...createPayload.workitem,
    workspace_id: createContext.workspaceId,
    team_id: createContext.teamId,
    id: generateUuid(),
  })

  const harmonyWorkitemRecord = {
    id: harmonyWorkitemId,
    harmony_package_id: createContext.packageId,
    workitem_id: workitemRecord.id,
    workspace_id: workitemRecord.workspace_id,
  } as HarmonyWorkitem

  const boardItemRecord = {
    id: boardWorkitemId,
    workitem_id: harmonyWorkitemId,
    board_id: createContext.boardId,
    column_id: createPayload.columnId,
    position,
    workspace_id: createContext.workspaceId,
  } as HarmonyPackageBoardWorkitem

  const createWorkitemRequestBody = {
    workitemRecord,
    harmonyWorkitemRecord,
    boardItemRecord,
  }

  let optimisticBoardItem: HarmonyPackageBoardWorkitem | undefined

  const addItemTx = createOptimisticAction<typeof createWorkitemRequestBody>({
    onMutate: (payloads: typeof createWorkitemRequestBody) => {
      optimisticBoardItem = payloads.boardItemRecord
      workitemsCollection.insert(payloads.workitemRecord)
      packageItemsCollection.insert(payloads.harmonyWorkitemRecord)
      boardItemsCollection.insert(payloads.boardItemRecord)
      return optimisticBoardItem
    },
    mutationFn: async (payloads: typeof createWorkitemRequestBody, params: any) => {
      const waitables = []
      // Persist the todo to your backend
      logger.debug('--Persisting new board item to backend', {
        payloads,
        params,
      })
      const persistedWorkitem = await $fetch<{ txid?: string }>('/api/workitems', {
        method: 'POST',
        body: payloads.workitemRecord,
      })
      logger.debug('Workitem persisted', { persistedWorkitem })
      if (persistedWorkitem?.txid && workitemsCollection.utils?.awaitTxId) {
        waitables.push(workitemsCollection.utils.awaitTxId(Number.parseInt(persistedWorkitem.txid), 300000))
      }

      const persistedHarmonyWorkitem = await $fetch<{ txid?: string }>('/api/harmony-workitem', {
        method: 'POST',
        body: payloads.harmonyWorkitemRecord,
      })
      logger.debug('Harmony workitem persisted', { persistedHarmonyWorkitem })
      if (persistedHarmonyWorkitem?.txid && packageItemsCollection.utils?.awaitTxId) {
        waitables.push(packageItemsCollection.utils.awaitTxId(Number.parseInt(persistedHarmonyWorkitem.txid), 300000))
      }

      const persistedBoardItem = await $fetch<{ txid?: string }>('/api/board-workitems', {
        method: 'POST',
        body: payloads.boardItemRecord,
      })
      logger.debug('Board workitem persisted', { persistedBoardItem })
      if (persistedBoardItem?.txid && boardItemsCollection.utils?.awaitTxId) {
        waitables.push(boardItemsCollection.utils.awaitTxId(Number.parseInt(persistedBoardItem.txid), 300000))
      }

      if (waitables.length === 0) return { txid: undefined }

      await Promise.all(waitables)
      return { txid: undefined }
    },
  })

  const tx = addItemTx(createWorkitemRequestBody)
  return tx
}
